import { get, post } from '../../../../GlobalModules/Utility/Http';

const PRODUCT_BASE_URL = `${process.env.EXTENDED_URL}product`;
const CATEGORY_BASE_URL = `${process.env.EXTENDED_URL}category`;
const RESTAURANT_TABLE_BASE_URL = `${process.env.EXTENDED_URL}restaurant_table`;
const STORE_BASE_URL = `${process.env.EXTENDED_URL}store`;

/**
 * Fetch categories for digital menu with webhook authentication
 * @param {Object} filters - Filter parameters for the API call
 * @returns {Promise} Processed category list data
 */
export const getDigitalMenuCategories = async (filters = {}) => {
  try {
    const filtersObj = {
      store_id: localStorage.getItem('digitalmenu_store_id') || localStorage.getItem('store_id'),
      display_on_pos_screen: 1,
      status: filters.status || 1,
      category_type: filters.category_type || 'food'
    };
    
    const queryParams = {
      filters: JSON.stringify(filtersObj)
    };
    
    // Use webhook authentication header
    const response = await get(`${CATEGORY_BASE_URL}`, queryParams, "webhook");

    if (!response || !response.data || !response.data.success) {
      return {
        success: false,
        message: response?.data?.message || 'Failed to fetch categories',
        data: []
      };
    }

    return {
      success: true,
      message: response.data.message || 'Categories fetched successfully',
      data: response.data.data || []
    };
  } catch (error) {
    console.error('Error fetching digital menu categories:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch categories',
      data: []
    };
  }
};

/**
 * Fetch products for digital menu with webhook authentication
 * @param {Object} filters - Filter parameters for the API call
 * @param {Object} filterOp - Filter operation parameters for the API call
 * @returns {Promise} Processed product list data
 */
export const getDigitalMenuProducts = async (filters = {}, filterOp = null) => {
  try {
    // Format filters according to the API structure
    let queryParams = {};
    // console.log('digitalmenu_store_id--->', localStorage.getItem('digitalmenu_store_id'));
    // console.log('Digital menu filterOp--->', filterOp);
    
    if (filters) {
      // Create filter object
      const filtersObj = {
        // limit: filters.limit || 100,
        // offset: filters.offset || 0,
        store_id: localStorage.getItem('digitalmenu_store_id') || localStorage.getItem('store_id') || "",
        is_ingredient: filters.is_ingredient !== undefined ? filters.is_ingredient : 0,
        is_addon_product: filters.is_addon_product !== undefined ? filters.is_addon_product : 0,
        status: filters.status || 1,
        exclude_alcoholic: "true"
      };

      // Handle category filter
      if (filters.category_id && Array.isArray(filters.category_id) && filters.category_id.length > 0) {
        filtersObj.category_id = filters.category_id;
      }

      // Handle search term - support both old 'search' and new 'search_attr'
      if (filters.search) {
        queryParams.filter_op = JSON.stringify({ search_attr: "substring" });
        filtersObj.search_attr = filters.search;
      } else if (filters.search_attr) {
        // New search_attr parameter support
        filtersObj.search_attr = filters.search_attr;
        
        // Add filter_op if provided
        if (filterOp) {
          queryParams.filter_op = JSON.stringify(filterOp);
        } else {
          queryParams.filter_op = JSON.stringify({ search_attr: "substring" });
        }
      }

      queryParams.filters = JSON.stringify(filtersObj);
    }

    // Use webhook authentication header
    const response = await get(`${PRODUCT_BASE_URL}/list`, queryParams, "webhook");

    if (!response || !response.data || !response.data.success) {
      return {
        success: false,
        message: response?.data?.message || 'Failed to fetch products',
        data: []
      };
    }

    return {
      success: true,
      message: response.data.message || 'Products fetched successfully',
      data: response.data.data || []
    };
  } catch (error) {
    console.error('Error fetching digital menu products:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch products',
      data: []
    };
  }
};

/**
 * Fetch restaurant table details for digital menu with webhook authentication
 * @param {string|number} id - Restaurant table ID
 * @returns {Promise} Processed restaurant table details
 */
export const getDigitalMenuTableDetails = async (id) => {
  try {
    // Use webhook authentication header
    const response = await get(`${RESTAURANT_TABLE_BASE_URL}/${id}`, {}, "webhook");
    
    if (!response || !response.data || !response.data.success) {
      return {
        success: false,
        message: response?.data?.message || 'Failed to fetch table details',
        data: null
      };
    }

    return {
      success: true,
      message: response.data.message || 'Table details fetched successfully',
      data: response.data.data || null
    };
  } catch (error) {
    console.error('Error fetching digital menu table details:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch table details',
      data: null
    };
  }
};

/**
 * Fetch store details for digital menu with webhook authentication
 * @param {string} storeSlack - Store slack identifier
 * @returns {Promise} Processed store details data
 */
export const getDigitalMenuStoreDetails = async (storeSlack) => {
  try {
    // Use webhook authentication header
    const response = await get(`${STORE_BASE_URL}/${storeSlack}`, {}, "webhook");
    
    if (!response || !response.data || !response.data.success) {
      return {
        success: false,
        message: response?.data?.message || 'Failed to fetch store details',
        data: null
      };
    }

    return {
      success: true,
      message: response.data.message || 'Store details fetched successfully',
      data: response.data.data || null
    };
  } catch (error) {
    console.error('Error fetching digital menu store details:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch store details',
      data: null
    };
  }
};

/**
 * Create menu QR code for digital menu with webhook authentication
 * @param {Object} params - Menu QR code params
 * @returns {Promise} Processed menu QR details
 */
export const createDigitalMenuQrCode = async (params) => {
  try {
    // Use webhook authentication header
    const response = await post(`${RESTAURANT_TABLE_BASE_URL}/generate_menu_qr`, params, "webhook");
    
    if (!response || !response.data || !response.data.success) {
      return {
        success: false,
        message: response?.data?.message || 'Failed to create menu QR code',
        data: null
      };
    }

    return {
      success: true,
      message: response.data.message || 'Menu QR code created successfully',
      data: response.data.data || null
    };
  } catch (error) {
    console.error('Error creating digital menu QR code:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to create menu QR code',
      data: null
    };
  }
};

export default {
  getDigitalMenuCategories,
  getDigitalMenuProducts,
  getDigitalMenuTableDetails,
  getDigitalMenuStoreDetails,
  createDigitalMenuQrCode
};
